import { StageCreationRequest, StageDetail } from '@portal/pkg-api/gen/stages'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { StagesAdminApi } from '@portal/pkg-api/admin'
import { idArrayToIndexMap } from '@portal/pkg-utils'
import { data } from '@portal/pkg-api/shared'

const prefix = 'admin.stages'

export const StageQueries = {
  stages: (entityTypeId: number) => ({
    queryKey: [`${prefix}.stages`, entityTypeId],
    queryFn: () => data(StagesAdminApi.getStages(entityTypeId)),
  }),
}

export const newEmptyStage: StageCreationRequest = {
  name: '',
  entityType: -1,
}

export function useStagesForEntityType(entityTypeId: number) {
  return useQuery(StageQueries.stages(entityTypeId))
}

export function useSaveStage() {
  const queryClient = useQueryClient()
  return useMutation<StageDetail, unknown, StageDetail>({
    mutationFn: (stage) => data(StagesAdminApi.updateStage(stage.id, stage)),
    onSuccess: ({ entityType: entityTypeId }) => {
      void queryClient.invalidateQueries(StageQueries.stages(entityTypeId))
    },
  })
}

export const reorderStages = async (stages: StageDetail[]) => {
  const reorderMap = idArrayToIndexMap(stages)
  return await data(StagesAdminApi.orderStages(reorderMap))
}

export function useReorderStages() {
  const queryClient = useQueryClient()
  return useMutation<void, unknown, StageDetail[]>({
    mutationFn: async (stages) => {
      const reorderMap = idArrayToIndexMap(stages)
      return await data(StagesAdminApi.orderStages(reorderMap))
    },
    onSuccess: (_data, stages) => {
      void queryClient.invalidateQueries(StageQueries.stages(stages[0].entityType))
    },
  })
}

export function useAddStage() {
  const queryClient = useQueryClient()

  return useMutation<StageDetail, unknown, StageDetail>({
    mutationFn: (stage) => data(StagesAdminApi.createStage(stage)),
    onSuccess: (stage) => {
      void queryClient.invalidateQueries(StageQueries.stages(stage.entityType))
    },
  })
}

export function useDeleteStage() {
  const queryClient = useQueryClient()

  return useMutation<void, unknown, StageDetail>({
    mutationFn: (stage) => data(StagesAdminApi.deleteStage(stage.id)),
    onSuccess: (_data, stage) => {
      void queryClient.invalidateQueries(StageQueries.stages(stage.entityType))
    },
  })
}
